import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useContext, useEffect, useState } from 'react'
import { buildWellnessTilePath } from 'src/consumer/helpers/wellnessRoutes'
import { preloadHeroCardImages } from 'src/utils/imagePreloader'
import { getCourseCardDetails } from 'src/consumer/helpers/courseCardHelper'

dayjs.extend(utc)
dayjs.extend(timezone)
import ReactPlayer from 'react-player'
import { useNavigate, useParams } from 'react-router-dom'
import {
  APP_CONSTANTS,
  HERO_CARD,
  supportedTileV2ItemTypes,
  unSupportedItemTypes
} from '../../constants'
import { SetupContext } from '../../context/SetupContext'
import { getTabOrder } from '../../helpers/tabDataHelper'
import { getTilesData } from '../../helpers/tilesDataHelper'
import { useFetch } from '../../services/index'
import { HeroCards } from '../HeroCards'
import { LoadingSpinner } from '../Loaders'
import Tile from '../Tile'
import TileV2 from '../Tile/TileV2'
import {
  BackgroundTransition,
  StyledTilesList,
  StyledTilesPageContainer,
  TileContainer,
  TilesPageBackground,
  TilesPageEmptyState,
  TilesPageImage,
} from './TilesPageStyle'

const TilesPage = (props) => {
  const { tileId, policyId } = useParams()
  const navigate = useNavigate()
  const [isMute, setIsMute] = useState(true)
  const { setupData } = useContext(SetupContext)
  const defaultTab = getTabOrder(setupData)?.preselectedTab
  const activeTileId = tileId || defaultTab
  const homeTileId = getHomeTileId(setupData)
  
  // Convert timezone offset to ISO 8601 format (+HH:MM or -HH:MM)
  const getTimezoneOffsetISO = () => {
    const offsetMinutes = new Date().getTimezoneOffset()
    // getTimezoneOffset returns negative for timezones ahead of UTC
    // So we need to invert the sign for ISO 8601 format
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
    const offsetMins = Math.abs(offsetMinutes) % 60
    const sign = offsetMinutes <= 0 ? '+' : '-'
    return `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`
  }
  
  const offset = encodeURIComponent(getTimezoneOffsetISO())
  let lat = 0
  let lng = 0
  if (localStorage.getItem('geoLocation') && localStorage.getItem('geoLocation') !== 'undefined') {
    try {
      const geoLocation = JSON.parse(localStorage.getItem('geoLocation'))
      lat = geoLocation?.lat || 0
      lng = geoLocation?.lng || 0
    } catch {
      localStorage.removeItem('geoLocation')
    }
  }
  const { loading, data, error } = useFetch(
    '/api/secure/tile',
    `&tileType=${activeTileId}&policyId=${
      policyId || ''
    }&zoneOffset=${offset}&lat=${lat}&lng=${lng}`,
    'web',
  )

  const shouldRedirectEmptyTileToHome = Boolean(
    !loading &&
      !error &&
      data &&
      activeTileId &&
      homeTileId &&
      activeTileId !== homeTileId &&
      !hasRenderableTileContent(data),
  )

  // Preload first hero card image for LCP optimization
  // This helps the browser discover and start loading the LCP image earlier
  // IMPORTANT: This hook must be called BEFORE any early returns to follow Rules of Hooks
  useEffect(() => {
    if (!data) return // Early return inside hook is fine
    
    let { tilesList } = getTilesData(data)
    if (!Array.isArray(tilesList)) {
      tilesList = []
    }

    const supportedHeroCard =
      tilesList[0]?.categoryType === HERO_CARD
        ? tilesList[0]?.items?.filter((card) => card?.type === 'course') || []
        : []
    const hasHeroCard = supportedHeroCard?.length > 0

    if (hasHeroCard && supportedHeroCard?.length > 0) {
      const firstCard = supportedHeroCard[0]
      if (firstCard) {
        const { heroCardLandscapeImage, heroCardPortraitImage } = getCourseCardDetails(firstCard)
        
        if (heroCardLandscapeImage || heroCardPortraitImage) {
          const landscapeUrl = heroCardLandscapeImage 
            ? `${APP_CONSTANTS.MEDIA_URL}${heroCardLandscapeImage}`
            : null
          const portraitUrl = heroCardPortraitImage
            ? `${APP_CONSTANTS.MEDIA_URL}${heroCardPortraitImage}`
            : null
          
          preloadHeroCardImages(landscapeUrl, portraitUrl)
        }
      }
    }
    // Only re-run when data changes (data comes from API)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!shouldRedirectEmptyTileToHome) return

    navigate(buildWellnessTilePath(homeTileId), { replace: true })
  }, [homeTileId, navigate, shouldRedirectEmptyTileToHome])

  if (loading || shouldRedirectEmptyTileToHome) return <LoadingSpinner />

  if (error || !data) {
    console.error('Error loading tiles data:', error)
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unable to load content</h2>
        <p>Please try refreshing the page.</p>
      </div>
    )
  }

  let { preSelectedBackground, videoURL, tilesList, userData, tilePolicyId } = getTilesData(data)

  // Ensure tilesList is always an array
  if (!Array.isArray(tilesList)) {
    tilesList = []
  }

  const supportedHeroCard =
    tilesList[0]?.categoryType === HERO_CARD
      ? tilesList[0]?.items?.filter((card) => card?.type === 'course') || []
      : []
  //kept herocard data aside
  const hasHeroCard = supportedHeroCard?.length > 0

  // adding allDailyFeaturesData into today's daily play
  let allDailyFeaturesData
  tilesList?.forEach((item) => {
    if (item?.itemType === 'dailyFeature' && item?.categoryType === 'allDailyFeatures') {
      allDailyFeaturesData = item
    }
  })
  if (allDailyFeaturesData) {
    tilesList?.forEach((item) => {
      if (item?.itemType === 'dailyFeature' && item?.categoryType === 'dailyFeature') {
        item.allDailyFeatures = allDailyFeaturesData
      }
    })
  }

  const supportedTilesList = (Array.isArray(tilesList) ? tilesList : []).filter((tiles) => {
    if (tiles?.itemType === 'activity-plan' && tiles?.categoryType === 'activePlan') {
      return true
    }
    // Allow dailyFeature tiles even if items array is empty, as they use allDailyFeatures data
    if (tiles?.itemType === 'dailyFeature' && tiles?.categoryType === 'dailyFeature') {
      const hasItems = tiles?.items && Array.isArray(tiles.items) && tiles.items.length > 0
      const hasAllDailyFeatures = tiles?.allDailyFeatures?.items && Array.isArray(tiles.allDailyFeatures.items) && tiles.allDailyFeatures.items.length > 0
      const hasGlobalAllDailyFeatures = allDailyFeaturesData?.items && Array.isArray(allDailyFeaturesData.items) && allDailyFeaturesData.items.length > 0
      return (
        tiles?.categoryType !== HERO_CARD &&
        tiles?.categoryType !== 'allDailyFeatures' &&
        !unSupportedItemTypes?.includes(tiles?.itemType) &&
        (hasItems || hasAllDailyFeatures || hasGlobalAllDailyFeatures)
      )
    }
    // For other tiles, require non-empty items array
    return (
      tiles?.categoryType !== HERO_CARD &&
      tiles?.categoryType !== 'allDailyFeatures' &&
      tiles?.items &&
      Array.isArray(tiles.items) &&
      tiles?.items?.length !== 0 &&
      !unSupportedItemTypes?.includes(tiles?.itemType)
    )
  })
  // no Herocards, no tiles with items array empty(except when plans and dailyFeature with allDailyFeatures) and no unsupported

  //moving tutorial card from position 1 to 2
  if (Array.isArray(supportedTilesList) && supportedTilesList.length > 0 && supportedTilesList[0]?.categoryType === 'tutorial') {
    let tutorialTile = supportedTilesList?.shift()
    if (tutorialTile) {
      supportedTilesList?.splice(1, 0, tutorialTile)
    }
  }

  let tileOnBackgroundArray = []
  if (!hasHeroCard && Array.isArray(supportedTilesList) && supportedTilesList.length > 0) {
    tileOnBackgroundArray = supportedTilesList?.splice(0, 1) || []
  }

  const renderedTilesCount = (tileOnBackgroundArray?.length || 0) + (supportedTilesList?.length || 0)
  const shouldShowEmptyState = !hasHeroCard && renderedTilesCount === 0
  const emptyStateTitle = data?.title || 'No wellness content available'
  const emptyStateDescription = data?.description || 'Please try refreshing this page.'

  return (
    <StyledTilesPageContainer>
      <div>
        {hasHeroCard ? (
          <HeroCards cardsData={supportedHeroCard} />
        ) : (
          <TilesPageBackground
            $backgroundColor={preSelectedBackground?.bgColor || preSelectedBackground?.secondaryColor}
          >
            {videoURL !== '' ? (
              <>
                {/* <MuteUnmuteIcon src={isMute ? Volume_Mute_Icon : Volume_Up_Icon} onClick={() => setIsMute(!isMute)} /> */}
                <ReactPlayer
                  playing
                  muted={isMute}
                  loop={false}
                  width="100%"
                  height="100%"
                  playbackRate={1}
                  url={videoURL}
                />
              </>
            ) : (
              preSelectedBackground?.imageId && (
                <TilesPageImage 
                  src={APP_CONSTANTS.MEDIA_URL + preSelectedBackground.imageId}
                  alt="Tiles page background"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              )
            )}

            {shouldShowEmptyState && (
              <TilesPageEmptyState>
                <h1>{emptyStateTitle}</h1>
                <p>{emptyStateDescription}</p>
              </TilesPageEmptyState>
            )}

            {tileId && tileId !== 'play' && <BackgroundTransition />}

            {tileOnBackgroundArray && tileOnBackgroundArray.length > 0 && (
              <TileContainer>
                {renderTilesList(
                  tileOnBackgroundArray,
                  userData,
                  activeTileId,
                  policyId,
                  tilePolicyId,
                  true,
                )}
              </TileContainer>
            )}
          </TilesPageBackground>
        )}

        <StyledTilesList>
          {renderTilesList(
            supportedTilesList,
            userData,
            activeTileId,
            policyId,
            tilePolicyId,
            false,
          )}
        </StyledTilesList>
      </div>
    </StyledTilesPageContainer>
  )
}

export default TilesPage

const HOME_TILE_ID = 'me'

const getHomeTileId = (setupData) => {
  const tabTiles = Array.isArray(setupData?.tabTiles) ? setupData.tabTiles : []
  const tabOrder = getTabOrder(setupData)
  const visibleTabs = new Set([...(tabOrder?.tabs || []), ...(tabOrder?.tiles || [])])

  const homeTile = tabTiles.find((tabTile) => {
    const title = typeof tabTile?.title === 'string' ? tabTile.title.trim().toLowerCase() : ''
    return tabTile?.id === HOME_TILE_ID || title === 'home'
  })

  if (homeTile?.id && (visibleTabs.size === 0 || visibleTabs.has(homeTile.id))) {
    return homeTile.id
  }

  return HOME_TILE_ID
}

const hasRenderableTileContent = (data) => {
  let { tilesList } = getTilesData(data)
  if (!Array.isArray(tilesList)) {
    tilesList = []
  }

  const supportedHeroCard =
    tilesList[0]?.categoryType === HERO_CARD
      ? tilesList[0]?.items?.filter((card) => card?.type === 'course') || []
      : []

  if (supportedHeroCard?.length > 0) {
    return true
  }

  const allDailyFeaturesData = tilesList.find((item) => {
    return item?.itemType === 'dailyFeature' && item?.categoryType === 'allDailyFeatures'
  })

  return tilesList.some((tiles) => {
    if (tiles?.itemType === 'activity-plan' && tiles?.categoryType === 'activePlan') {
      return true
    }

    if (tiles?.itemType === 'dailyFeature' && tiles?.categoryType === 'dailyFeature') {
      const hasItems = tiles?.items && Array.isArray(tiles.items) && tiles.items.length > 0
      const hasAllDailyFeatures = tiles?.allDailyFeatures?.items && Array.isArray(tiles.allDailyFeatures.items) && tiles.allDailyFeatures.items.length > 0
      const hasGlobalAllDailyFeatures = allDailyFeaturesData?.items && Array.isArray(allDailyFeaturesData.items) && allDailyFeaturesData.items.length > 0
      return (
        tiles?.categoryType !== HERO_CARD &&
        tiles?.categoryType !== 'allDailyFeatures' &&
        !unSupportedItemTypes?.includes(tiles?.itemType) &&
        (hasItems || hasAllDailyFeatures || hasGlobalAllDailyFeatures)
      )
    }

    return (
      tiles?.categoryType !== HERO_CARD &&
      tiles?.categoryType !== 'allDailyFeatures' &&
      tiles?.items &&
      Array.isArray(tiles.items) &&
      tiles?.items?.length !== 0 &&
      !unSupportedItemTypes?.includes(tiles?.itemType)
    )
  })
}

const renderTilesList = (
  supportedTilesList,
  userData,
  tileId,
  policyId,
  tilePolicyId,
  isOnBackground,
) => {
  if (!Array.isArray(supportedTilesList) || supportedTilesList.length === 0) {
    return null
  }
  return supportedTilesList.map((tile, index) => {
    return supportedTileV2ItemTypes.includes(tile?.itemType) ? (
      <TileV2
        key={index}
        tileData={tile}
        userData={userData}
        tileId={tileId}
        policyId={policyId}
        tilePolicyId={tilePolicyId}
        isOnBackground={isOnBackground}
      />
    ) : (
      <Tile
        key={index}
        tileData={tile}
        userData={userData}
        tileId={tileId}
        policyId={policyId}
        tilePolicyId={tilePolicyId}
        isOnBackground={isOnBackground}
      />
    )
  })
}
